import { CheckIcon as CentralCheckIcon } from "@wags/ui/product-icons.tsx";
import type { ComponentType } from "react";
import { cn } from "@wags/ui/ui-utils";
import { BoxGlyph, PinGlyph, PlaneGlyph, VanGlyph } from "./glyphs";
import type {
    TransportModeKind,
    TransportModuleView,
    TransportStateChip,
} from "./transport";

const GLYPHS: Record<
    TransportModeKind,
    ComponentType<{ className?: string }>
> = {
    pickup: PinGlyph,
    ground: VanGlyph,
    flight_nanny: PlaneGlyph,
    air_cargo: BoxGlyph,
};

function CheckGlyph({ className }: { className?: string }) {
    return <CentralCheckIcon aria-hidden="true" className={className} />;
}

function MeetupChips({ cities }: { cities: string[] }) {
    const shown = cities.slice(0, 8);
    const more = cities.length - shown.length;
    return (
        <div className="flex flex-wrap gap-1.5">
            {shown.map((city) => (
                <span
                    key={city}
                    className="rounded-label border border-doberman-black/10 px-2 py-1 text-caption font-medium text-doberman-black/70"
                >
                    {city}
                </span>
            ))}
            {more > 0 && (
                <span className="px-2 py-1 text-caption font-medium text-doberman-black/50">
                    +{more} more
                </span>
            )}
        </div>
    );
}

function StateChips({ chips }: { chips: TransportStateChip[] }) {
    const shown = chips.slice(0, 12);
    const more = chips.length - shown.length;
    return (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
            {shown.map((chip) => (
                <span
                    key={chip.code}
                    className={cn(
                        "flex items-center gap-1 rounded-label px-2 py-1 text-caption font-semibold",
                        chip.yours
                            ? "border border-labrador-yellow bg-labrador-yellow/15 text-doberman-black"
                            : "border border-doberman-black/10 text-doberman-black/70",
                    )}
                >
                    {chip.yours && (
                        <CheckGlyph className="size-3 shrink-0 text-chocolate-lab" />
                    )}
                    {chip.code}
                </span>
            ))}
            {more > 0 && (
                <span className="px-2 py-1 text-caption font-medium text-doberman-black/50">
                    +{more} more
                </span>
            )}
        </div>
    );
}

export function TransportModule({ view }: { view: TransportModuleView }) {
    const { map, hero, alts, emptyLine } = view;
    const HeroGlyph = hero ? GLYPHS[hero.kind] : null;

    return (
        <div className="flex flex-col gap-3 rounded-card bg-true-white p-card border-shadow">
            {map?.src ? (
                <div className="relative h-44.75 w-full overflow-hidden rounded-nested">
                    <img
                        src={map.src}
                        alt={map.chipLabel ? `Map of ${map.chipLabel}` : "Map"}
                        className="absolute inset-0 size-full object-cover"
                    />
                    {map.chipLabel && (
                        <span className="absolute left-3 top-3 max-w-[calc(100%-1.5rem)] truncate rounded-label bg-true-white px-2.5 py-1.5 text-body-small font-medium text-doberman-black shadow-transport-chip">
                            {map.chipLabel}
                        </span>
                    )}
                </div>
            ) : map?.chipLabel ? (
                <div className="flex items-center gap-2 px-1 py-0.5">
                    <PinGlyph className="size-4 shrink-0 text-doberman-black/50" />
                    <span className="truncate text-body-small font-medium text-doberman-black">
                        {map.chipLabel}
                    </span>
                </div>
            ) : null}

            {emptyLine ? (
                <p className="px-1 text-body-small text-doberman-black/65">
                    {emptyLine}
                </p>
            ) : (
                <div className="flex flex-col divide-y divide-doberman-black/[0.06] overflow-hidden rounded-nested border border-doberman-black/[0.06] bg-true-white">
                    {hero && HeroGlyph && (
                        <div className="flex flex-col gap-2.5 p-4">
                            <div className="flex items-center gap-3">
                                <HeroGlyph className="size-4 shrink-0 text-doberman-black" />
                                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                                    <p className="text-body font-semibold text-doberman-black">
                                        {hero.title}
                                    </p>
                                    {hero.subtitle && (
                                        <p className="text-body-small text-doberman-black/65">
                                            {hero.subtitle}
                                        </p>
                                    )}
                                </div>
                                {hero.recommended && (
                                    <span className="shrink-0 rounded-label bg-doberman-black/[0.06] px-2 py-0.5 text-caption font-semibold text-doberman-black/70">
                                        Recommended
                                    </span>
                                )}
                            </div>
                            {hero.stateChips && (
                                <div className="pl-7">
                                    <StateChips chips={hero.stateChips} />
                                </div>
                            )}
                            {hero.meetups && hero.meetups.length > 0 && (
                                <div className="pl-7">
                                    <MeetupChips cities={hero.meetups} />
                                </div>
                            )}
                        </div>
                    )}
                    {alts.map((alt) => {
                        const Glyph = GLYPHS[alt.kind];
                        return (
                            <div
                                key={`${alt.kind}-${alt.title}`}
                                className={cn(
                                    "flex gap-3 px-4 py-2.5",
                                    alt.stateChips
                                        ? "items-start"
                                        : "items-center",
                                )}
                            >
                                <Glyph
                                    className={cn(
                                        "size-4 shrink-0 text-doberman-black",
                                        alt.stateChips && "mt-0.5",
                                    )}
                                />
                                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                                    <p className="text-body font-medium text-doberman-black">
                                        {alt.title}
                                    </p>
                                    {alt.subtitle && (
                                        <p className="text-body-small text-doberman-black/65">
                                            {alt.subtitle}
                                        </p>
                                    )}
                                    {alt.stateChips && (
                                        <StateChips chips={alt.stateChips} />
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
